import {
  getPresetDate,
  isCustomDateSelectorPreset,
} from '@app/components/date-range-selector/single-date/date-selector-presets';
import type { SingleDateFilterValue } from './parse-as-single-date-filter';
import moment from 'moment-timezone';

/**
 * Resolves a single-date filter value to the selected calendar day (start of day in timezone).
 */
export function singleDateFilterValueToDate(
  value: SingleDateFilterValue | null | undefined,
  timezone: string,
): Date | null {
  if (value == null) {
    return null;
  }

  if (isCustomDateSelectorPreset(value.presetId) && value.date) {
    return moment(value.date).tz(timezone).startOf('day').toDate();
  }

  return getPresetDate(value.presetId, timezone);
}

/**
 * Returns day bounds [start, end) for the selected calendar day in timezone.
 */
export function singleDateFilterValueToDayRange(
  value: SingleDateFilterValue | null | undefined,
  timezone: string,
): { from: Date; to: Date } | null {
  const day = singleDateFilterValueToDate(value, timezone);
  if (!day) {
    return null;
  }

  const from = moment(day).tz(timezone).startOf('day');
  const to = from.clone().add(1, 'day');

  return { from: from.toDate(), to: to.toDate() };
}

/**
 * Resolves a single-date filter to ISO bounds for CRUD `createdAt` filters (inclusive day in timezone).
 */
export function singleDateFilterValueToIsoRange(
  value: SingleDateFilterValue | null | undefined,
  timezone: string,
): { from?: string; to?: string } {
  const dayRange = singleDateFilterValueToDayRange(value, timezone);
  if (!dayRange) {
    return {};
  }

  const from = moment(dayRange.from).tz(timezone).startOf('day');
  const to = from.clone().endOf('day');

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
}
